type NotificationKind =
  'error' | 'info' | 'info-square' | 'success' | 'warning' | 'warning-alt';
import request from 'lib/request';
import {
  type ForecastData,
  ProductionVolumeData,
  SeriesDragUpdate,
  type StreamConfigs,
  XAxisType,
} from '../types';
import { XAxisSwitcher } from './x-axis-switcher';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Button } from '@carbon/react';
import { Maximize, Minimize } from '@carbon/react/icons';
import style from './decline-curve-analysis.module.scss';
import {
  useDcaEditorProduction,
  useDeclineCurveAnalysis,
  useVolumeMetric,
} from '../hooks';
import {
  TypeCurveRatioRecord,
  TypeCurveVolumeRecord,
  WellPDPDeclineMetricData,
} from 'types/api-responses';
import type { WellProductionDataSources } from 'types/api-responses';
import { VolumeChart2 } from './volume-chart-2';
import { ChartExportSection } from 'components/chart-export';
import { MineralType } from 'types/minerals';
import { debounce, isNil, kebabCase } from 'lodash-es';
import { DeclineCurveAnalysisTable } from './decline-curve-analysis-table';
import { atom, useAtom, useAtomValue, useSetAtom, useStore } from 'jotai';
import {
  convertToHyperbolicErrorAtom,
  gasModelFormAtom,
  oilModelFormAtom,
  waterModelFormAtom,
  _lockParamsStateAtom,
  regressionDataAtom,
  useDcaFormData,
  isWaterModelValidAtom,
  isGasModelValidAtom,
  isOilModelValidAtom,
  lockParamsAtom,
  selectedTypeCurveAtom,
  clearDcaNotificationGroupAtom,
  setDcaNotificationAtom,
  selectedDcaScenarioAtom,
  typeCurveVolumesDataAtom,
  initialWaterModelValuesAtom,
  initialGasModelValuesAtom,
  initialOilModelValuesAtom,
  earlyTimeStreamsAtom,
  fittedProductionAtom,
  selectedRecordVersionAtom,
  resetRegressionDataAtom,
  wellboresMutationAtom,
  dcaProductionStreamConfigsAtom,
  pdpVersionsRequestParamsAtom,
  pdpVersionsForecastDataAtom,
  applyPdpForecastAtom,
  cancelApplyPdpForecastAtom,
  defaultModelFormValues,
  useProductionForecastsVersionsAtom,
} from '../atoms';
import { EurTable } from './eur-table';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import {
  calculateStreamConfigs,
  getInitialLockedParameters,
  isLocked,
} from '../utils';
import { parseMomentUtc, SERVER_DATE_FORMAT } from 'utils/dates';
import { ConfigurationPanel } from './configuration-panel';
import { DcaNotifications } from './dca-notifications';
import { useDcaHotkeysMode } from './hotkeys-mode/use-dca-hotkeys-mode';
import { HotkeysHelpOverlay } from './hotkeys-mode/hotkeys-help-overlay';
import { useFitCheckedStreams } from './fit-streams-selector';
import { ChartActions } from './chart-actions';
import type { ForecastVersion } from './pdp-forecast-version-selector';
import { DUMMY_DCA_SCENARIOS } from './dca-scenario-selector';
import type { DcaSaveAction } from './dca-save-action';

interface DeclineCurveAnalysisProps {
  uwi: string;
  wellName?: string;
  recordVersionHref: string;
  recordHref: string;
  /** Daily production and its versions; without them only IHS production is offered. */
  dailyRecordHref?: string;
  dailyRecordVersionHref?: string;
  /** The sources the well has production for and its default, or undefined while they load. Used only with the daily hrefs. */
  productionDataSources?: WellProductionDataSources;
  legacyPdpForecastVersionsHref: string;
  insightsPdpForecastVersionsHref: string;
  hideForecastTable?: boolean;
  /** Hotkeys mode is only offered inside the DCA tearsheet. */
  enableHotkeysMode?: boolean;
  /** Walk to the previous/next well in the wells-table display order. */
  onPreviousWell?: () => void;
  onNextWell?: () => void;
  /**
   * The forecast to open on, when it is in the selected DCA scenario. When it
   * is not, the editor opens on the scenario's newest forecast.
   */
  dcaForecastId?: string | null;
  dcaWorkspaceId?: number;
  onForecastSaved?: () => void;
  /** Takes over the configuration panel's Save button. */
  saveAction?: DcaSaveAction;
}

/**
 * The notification group that says the selected DCA scenario has no forecast
 * for the well, or that its forecasts could not be loaded.
 */
const SCENARIO_FORECAST_NOTIFICATIONS = 'scenario-forecast';

/** The streams with a model form. */
const MODEL_STREAMS = ['oil', 'gas', 'water'] as const;

/**
 * The streams a DCA scenario's settings fill the model form of.
 *
 * @param {StreamConfigs | null} streamConfigs - The scenario's settings for the well.
 * @return {MineralType[]} The streams with a decline model in the settings.
 */
const streamsWithSettings = (
  streamConfigs: StreamConfigs | null
): MineralType[] =>
  MODEL_STREAMS.filter((stream) => streamConfigs?.[`${stream}Config`]?.dca);

/** A filling of the model forms from a DCA scenario's settings. */
interface FormsFill {
  scenarioId: number | string | null;
  /** The streams the settings filled. The other streams' forms were left alone. */
  streams: MineralType[];
}

interface ShowMarkers {
  segmentation: boolean;
  outlier: boolean;
}

/** The four charts, in the order they fill the 2x2 grid. */
const CHART_PANELS = [
  { type: 'monthly', title: 'Monthly Volumes' },
  { type: 'logarithmic', title: 'Log Log Volumes' },
  { type: 'cumulative', title: 'Cumulative Volumes' },
  { type: 'ratio', title: 'Ratios' },
] as const;

type ChartPanelType = (typeof CHART_PANELS)[number]['type'];

/**
 * The x axis each chart opens on. The log chart has no date option, so it
 * starts on months online.
 */
const DEFAULT_X_AXIS_TYPES: Record<ChartPanelType, XAxisType> = {
  monthly: 'date',
  logarithmic: 'count',
  cumulative: 'date',
  ratio: 'date',
};

/**
 * Which chart, if any, is expanded to the full grid width. Module scope so it
 * survives the remount the tearsheet does when the arrows change well: an
 * expanded chart should stay expanded across that.
 */
const expandedChartAtom = atom<ChartPanelType | null>(null);

/** The legend entries that stand for a volume stream rather than a ratio. */
const VOLUME_STREAMS: MineralType[] = ['oil', 'gas', 'water'];

/** The stream each hotkey scope drives. */
const HOTKEY_SCOPE_STREAMS: Record<string, MineralType> = {
  DCA_INPUT_FORM_OIL: 'oil',
  DCA_INPUT_FORM_GAS: 'gas',
  DCA_INPUT_FORM_WATER: 'water',
};

const fallbackAtom = atom({
  value: null,
  isValid: true,
  isDirty: false,
  error: null,
});

const DeclineCurveAnalysisImpl = ({
  uwi,
  wellName,
  recordVersionHref,
  recordHref,
  dailyRecordHref,
  dailyRecordVersionHref,
  productionDataSources,
  legacyPdpForecastVersionsHref,
  insightsPdpForecastVersionsHref,
  hideForecastTable = false,
  enableHotkeysMode = false,
  onPreviousWell,
  onNextWell,
  dcaForecastId,
  dcaWorkspaceId,
  onForecastSaved,
  saveAction,
}: DeclineCurveAnalysisProps) => {
  const [expandedChart, setExpandedChart] = useAtom(expandedChartAtom);
  // Each chart picks its own x axis, since what reads well against a date on
  // one does not on another.
  const [xAxisTypes, setXAxisTypes] =
    useState<Record<ChartPanelType, XAxisType>>(DEFAULT_X_AXIS_TYPES);
  const regressionData = useAtomValue(regressionDataAtom);
  const isOilModelValid = useAtomValue(isOilModelValidAtom);
  const isGasModelValid = useAtomValue(isGasModelValidAtom);
  const isWaterModelValid = useAtomValue(isWaterModelValidAtom);
  const selectedTypeCurve = useAtomValue(selectedTypeCurveAtom);
  const selectedScenario = useAtomValue(selectedDcaScenarioAtom);
  const selectedRecordVersion = useAtomValue(selectedRecordVersionAtom);
  const setDcaNotifications = useSetAtom(setDcaNotificationAtom);
  const [typeCurveVolumeMetricResponse, fetchScaledTypeCurveVolumes] = useAtom(
    typeCurveVolumesDataAtom
  );
  const [earlyTimeStreams, setEarlyTimeStreams] = useState<MineralType[]>([]);
  const fetchEarlyTimeStreams = useSetAtom(earlyTimeStreamsAtom);
  const setInitialOilModelValues = useSetAtom(initialOilModelValuesAtom);
  const setInitialGasModelValues = useSetAtom(initialGasModelValuesAtom);
  const setInitialWaterModelValues = useSetAtom(initialWaterModelValuesAtom);
  const setFittedProduction = useSetAtom(fittedProductionAtom);
  const setDcaNotification = useSetAtom(setDcaNotificationAtom);
  const clearDcaGroupNotifications = useSetAtom(clearDcaNotificationGroupAtom);
  const resetRegressionData = useSetAtom(resetRegressionDataAtom);
  const [resetTable, setResetTable] = useState(false);
  const wellboresData = useAtomValue(wellboresMutationAtom);
  const [dcaProductionStreamConfigs, setDcaProductionStreamConfigs] = useAtom(
    dcaProductionStreamConfigsAtom
  );
  const store = useStore();
  // The last filling of the model forms from the selected scenario's settings.
  const formsFilledForRef = useRef<FormsFill | null>(null);

  const [showMarkers, setShowMarkers] = useState<ShowMarkers>({
    segmentation: true,
    outlier: true,
  });
  const [selectedLegend, setSelectedLegend] = useState<
    Array<MineralType | 'GOR' | 'WOR' | 'WGR' | 'CGR'>
  >(['oil', 'water', 'gas', 'GOR', 'WOR', 'WGR', 'CGR']);

  const [showTypeCurve, setShowTypeCurve] = useState<boolean>(false);

  const setPdpVersionsRequestParams = useSetAtom(pdpVersionsRequestParamsAtom);
  const [pdpVersionsLoading, setPdpVersionsLoading] = useState(false);
  const pdpVersionsForecastData = useAtomValue(pdpVersionsForecastDataAtom);
  const setPdpVersionsForecastData = useSetAtom(pdpVersionsForecastDataAtom);
  const pdpVolumesRequestIdRef = useRef<string>('');

  const [currentProduction, setCurrentProduction] = useState<{
    isDailySource: boolean;
    volume: ProductionVolumeData;
  } | null>(null);
  const currentProductionVolume = currentProduction?.volume;

  const [lockedParams, setLockParams] = useAtom(_lockParamsStateAtom);
  const lockParamsAction = useSetAtom(lockParamsAtom);

  const formData = useDcaFormData();
  const setForecastDuration = useSetAtom(
    formData.forecastSettingsForm.forecastDuration
  );
  const setForecastDurationOption = useSetAtom(
    formData.forecastSettingsForm.forecastDurationOption
  );
  const setQiFormat = useSetAtom(formData.forecastSettingsForm.qiFormat);
  const setDiFormat = useSetAtom(formData.forecastSettingsForm.diFormat);

  // Form-level setters to recreate entire form structure
  const setOilForm = useSetAtom(oilModelFormAtom);
  const setGasForm = useSetAtom(gasModelFormAtom);
  const setWaterForm = useSetAtom(waterModelFormAtom);

  const oilStartDate = useAtomValue(formData.oilModelForm.startDateAtomField);
  const gasStartDate = useAtomValue(formData.gasModelForm.startDateAtomField);
  const waterStartDate = useAtomValue(
    formData.waterModelForm.startDateAtomField
  );
  const startDateByStream = useMemo(
    () => ({
      oil: oilStartDate.value,
      gas: gasStartDate.value,
      water: waterStartDate.value,
    }),
    [oilStartDate.value, gasStartDate.value, waterStartDate.value]
  );
  const oilSetStartDate = useSetAtom(formData.oilModelForm.startDateAtomField);
  const gasSetStartDate = useSetAtom(formData.gasModelForm.startDateAtomField);
  const waterSetStartDate = useSetAtom(
    formData.waterModelForm.startDateAtomField
  );

  const oilSetQi = useSetAtom(formData.oilModelForm.qiAtomField);
  const gasSetQi = useSetAtom(formData.gasModelForm.qiAtomField);
  const waterSetQi = useSetAtom(formData.waterModelForm.qiAtomField);

  const oilSetRi = useSetAtom(
    formData.oilModelForm.riAtomField ?? fallbackAtom
  );
  const gasSetRi = useSetAtom(
    formData.gasModelForm.riAtomField ?? fallbackAtom
  );
  const waterSetRi = useSetAtom(
    formData.waterModelForm.riAtomField ?? fallbackAtom
  );

  const oilSetTElf = useSetAtom(
    formData.oilModelForm.tElfAtomField ?? fallbackAtom
  );
  const gasSetTElf = useSetAtom(
    formData.gasModelForm.tElfAtomField ?? fallbackAtom
  );
  const waterSetTElf = useSetAtom(
    formData.waterModelForm.tElfAtomField ?? fallbackAtom
  );

  const oilSetRampupDuration = useSetAtom(
    formData.oilModelForm.rampupDurationAtomField ?? fallbackAtom
  );
  const gasSetRampupDuration = useSetAtom(
    formData.gasModelForm.rampupDurationAtomField ?? fallbackAtom
  );
  const waterSetRampupDuration = useSetAtom(
    formData.waterModelForm.rampupDurationAtomField ?? fallbackAtom
  );
  const oilIsPrimary =
    formData.isOilModelValid && formData.oilDeclineModel.value !== 'ratio';
  const gasIsPrimary =
    formData.isGasModelValid && formData.gasDeclineModel.value !== 'ratio';
  const primaryStream: 'oil' | 'gas' | null = oilIsPrimary
    ? 'oil'
    : gasIsPrimary
      ? 'gas'
      : null;

  const [convertToHyperbolicError, setConvertToHyperbolicError] = useAtom(
    convertToHyperbolicErrorAtom
  );
  const applyPdpForecast = useSetAtom(applyPdpForecastAtom);
  const cancelApplyPdpForecast = useSetAtom(cancelApplyPdpForecastAtom);

  const draggableSeriesMap = useMemo(
    () => ({
      oil: {
        qi: oilSetQi,
        declineModel: formData.oilDeclineModel,
        rampupDuration: oilSetRampupDuration,
        rampupModel: formData.oilRampupModel,
      },
      gas: {
        qi: gasSetQi,
        declineModel: formData.gasDeclineModel,
        rampupDuration: gasSetRampupDuration,
        rampupModel: formData.gasRampupModel,
      },
      water: {
        qi: waterSetQi,
        declineModel: formData.waterDeclineModel,
        rampupDuration: waterSetRampupDuration,
        rampupModel: formData.waterRampupModel,
      },
      gor: {
        ri: formData.gasDeclineModel.value === 'ratio' ? gasSetRi : oilSetRi,
        tElf:
          formData.gasDeclineModel.value === 'ratio' ? gasSetTElf : oilSetTElf,
        declineModel: formData.oilDeclineModel,
      },
      wor: {
        ri: waterSetRi,
        tElf: waterSetTElf,
        declineModel: formData.waterDeclineModel,
      },
      wgr: {
        ri: waterSetRi,
        tElf: waterSetTElf,
        declineModel: formData.waterDeclineModel,
      },
      cgr: {
        ri: formData.oilDeclineModel.value === 'ratio' ? oilSetRi : gasSetRi,
        tElf:
          formData.oilDeclineModel.value === 'ratio' ? oilSetTElf : gasSetTElf,
        declineModel: formData.gasDeclineModel,
      },
    }),
    [
      oilSetQi,
      oilSetRi,
      oilSetTElf,
      formData.oilDeclineModel,
      oilSetRampupDuration,
      formData.oilRampupModel,
      gasSetQi,
      gasSetRi,
      gasSetTElf,
      formData.gasDeclineModel,
      gasSetRampupDuration,
      formData.gasRampupModel,
      waterSetQi,
      waterSetRi,
      waterSetTElf,
      formData.waterDeclineModel,
      waterSetRampupDuration,
      formData.waterRampupModel,
    ]
  );

  const markers = useMemo(() => {
    if (regressionData.loading) return null;

    const data =
      regressionData.data?.bulkDcaRegressionResults.find(
        (result) => result.uwi === uwi
      ) || null;

    if (!data) return null;

    const markersData: {
      segmentationDates: Record<MineralType, string[]>;
      outlierDates: Record<MineralType, string[]>;
    } = {
      segmentationDates: { oil: [], gas: [], water: [] },
      outlierDates: { oil: [], gas: [], water: [] },
    };

    Object.entries(data.results).forEach(([stream, result]) => {
      if (result?.segmentStartDates) {
        markersData.segmentationDates[stream as MineralType] =
          result.segmentStartDates;
      }
      if (result?.outlierDates) {
        markersData.outlierDates[stream as MineralType] = result.outlierDates;
      }
    });

    return markersData;
  }, [uwi, regressionData.loading, regressionData.data]);

  const {
    data: declineCurveAnalysisForecast,
    loading: isDeclineCurveAnalysisLoading,
    error: declineCurveAnalysisError,
    dcaForecast,
    isDcaForecastDirty,
    resetDeclineCurveAnalysis,
  } = useDeclineCurveAnalysis(uwi, selectedRecordVersion?.versionId ?? '');

  const declineCurveAnalysisVolumes =
    declineCurveAnalysisForecast?.pdpDeclines[uwi]?.[0]?.volumes;

  const isValidDcaForecast = useMemo(() => {
    return isOilModelValid || isGasModelValid || isWaterModelValid;
  }, [isOilModelValid, isGasModelValid, isWaterModelValid]);

  const selectedRecordVersions = useMemo(() => {
    if (selectedRecordVersion?.versionId) {
      return [{ label: selectedRecordVersion.name, ...selectedRecordVersion }];
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRecordVersion?.versionId]);

  const {
    data: volumeMetricResponse,
    loading: isVolumeMetricLoading,
    error: volumeMetricError,
    setVolumeMetricResponse,
  } = useVolumeMetric();

  const {
    data: eurMetricResponseData,
    loading: isEurMetricLoading,
    setVolumeMetricResponse: eurMetricResponse,
    resetVolumeMetric: resetEurMetricResponse,
  } = useVolumeMetric();

  const {
    data: declineCurveMetricResponse,
    loading: declineCurveMetricLoading,
    error: declineCurveMetricError,
    setVolumeMetricResponse: setDeclineCurveMetricResponse,
    resetVolumeMetric: resetDeclineCurveMetricResponse,
  } = useVolumeMetric();

  const {
    data: productionOnlyMetricResponse,
    loading: isProductionOnlyMetricLoading,
    setVolumeMetricResponse: setProductionOnlyMetricResponse,
  } = useVolumeMetric();

  const declineCurveAnalysisPartialErrors = useMemo(
    () => declineCurveAnalysisForecast?.pdpDeclines[uwi]?.[0]?.errors || [],
    [declineCurveAnalysisForecast, uwi]
  );

  const declineCurveMetricPartialError = useMemo(
    () => declineCurveAnalysisForecast?.pdpDeclines[uwi]?.[0]?.errors || [],
    [declineCurveAnalysisForecast, uwi]
  );

  const eurMetricResponsePartialError = useMemo(
    () => eurMetricResponseData?.pdpDeclines[uwi]?.[0]?.errors || [],
    [eurMetricResponseData, uwi]
  );

  // A response only ever holds the well it was fetched for, so every read is
  // keyed by uwi and has to tolerate the key being absent.
  const declineCurveMetricData = useMemo(
    () => declineCurveMetricResponse?.pdpDeclines[uwi]?.[0],
    [declineCurveMetricResponse, uwi]
  );

  const productionOnlyMetricData = useMemo(
    () => productionOnlyMetricResponse?.pdpDeclines[uwi]?.[0],
    [productionOnlyMetricResponse, uwi]
  );

  const combinedEurMetricData = useMemo(() => {
    const forecastMetrics =
      eurMetricResponseData?.pdpDeclines[uwi]?.[0]?.aggregationMetrics;

    const productionMetrics = productionOnlyMetricData?.aggregationMetrics;

    if (!forecastMetrics && !productionMetrics) return null;

    const eurTableData = forecastMetrics || productionMetrics;

    return {
      recoveredOil: productionMetrics?.recoveredOil ?? null,
      recoveredGas: productionMetrics?.recoveredGas ?? null,
      recoveredWater: productionMetrics?.recoveredWater ?? null,
      oilEur: eurTableData?.oilEur ?? null,
      gasEur: eurTableData?.gasEur ?? null,
      waterEur: eurTableData?.waterEur ?? null,
      remainingOil: eurTableData?.remainingOil ?? null,
      remainingGas: eurTableData?.remainingGas ?? null,
      remainingWater: eurTableData?.remainingWater ?? null,
      gasOilRatioTotal: eurTableData?.gasOilRatioTotal ?? null,
      waterCutTotal: eurTableData?.waterCutTotal ?? null,
      waterGasRatioTotal: eurTableData?.waterGasRatioTotal ?? null,
      oilGasRatioTotal: eurTableData?.oilGasRatioTotal ?? null,
      waterOilRatioTotal: eurTableData?.waterOilRatioTotal ?? null,
    };
  }, [eurMetricResponseData, productionOnlyMetricData, uwi]);

  const debouncedSetDeclineCurveMetricResponse = useMemo(
    () => debounce((response) => setDeclineCurveMetricResponse(response), 300),
    [setDeclineCurveMetricResponse]
  );

  const debouncedSetEurMetricResponse = useMemo(
    () => debounce((response) => eurMetricResponse(response), 300),
    [eurMetricResponse]
  );

  const {
    isDailySource,
    productionSource,
    productionSourceParam,
    volumeDisplayMode,
    setVolumeDisplayMode,
    showDailyRows,
    productionVolume,
    chartProductionVolume,
    isProductionLoading,
  } = useDcaEditorProduction({
    uwi,
    recordHref,
    dailyRecordHref,
    selectedRecordVersions,
  });

  // The first production read for the source, which the early-time and
  // first-production checks below work from. Switching the source reads it
  // again from the new source.
  useEffect(() => {
    if (
      productionVolume &&
      currentProduction?.isDailySource !== isDailySource
    ) {
      setCurrentProduction({ isDailySource, volume: productionVolume });
    }
  }, [productionVolume, currentProduction, isDailySource]);

  const isDummyScenario = useMemo(() => {
    return DUMMY_DCA_SCENARIOS.some(
      (dummy) => dummy.id === selectedScenario?.id
    );
  }, [selectedScenario?.id]);

  // A new DCA scenario starts on empty forms, without the last scenario's
  // forecast notice. Its settings and forecast fill them once they load. The
  // last scenario's forecast curves and EUR are cleared too, since empty forms
  // send no request to replace them, and if the new scenario has no forecast
  // they would stay drawn. The last scenario's forecast apply, if still
  // loading, is cancelled, since the new scenario may have no forecast to apply
  // in its place.
  useEffect(() => {
    cancelApplyPdpForecast();
    clearDcaGroupNotifications(SCENARIO_FORECAST_NOTIFICATIONS);
    resetDeclineCurveAnalysis();
    resetEurMetricResponse();
    resetDeclineCurveMetricResponse();
    setOilForm(defaultModelFormValues);
    setGasForm(defaultModelFormValues);
    setWaterForm(defaultModelFormValues);
  }, [
    selectedScenario?.id,
    cancelApplyPdpForecast,
    clearDcaGroupNotifications,
    resetDeclineCurveAnalysis,
    resetEurMetricResponse,
    resetDeclineCurveMetricResponse,
    setOilForm,
    setGasForm,
    setWaterForm,
  ]);

  useEffect(() => {
    if (!selectedScenario?.id || !uwi) return;

    if (!isDummyScenario) {
      setDcaProductionStreamConfigs({
        scenarioId: selectedScenario.id,
        uwi: uwi,
      });
    }
  }, [
    selectedScenario?.id,
    setDcaProductionStreamConfigs,
    uwi,
    isDummyScenario,
  ]);

  // Populate DCA form fields when a scenario is loaded
  useEffect(() => {
    if (
      !dcaProductionStreamConfigs?.data ||
      'error' in dcaProductionStreamConfigs.data ||
      dcaProductionStreamConfigs.loading ||
      !currentProductionVolume?.volumes ||
      currentProductionVolume?.volumes.length === 0
    ) {
      return;
    }

    const streamConfigs = calculateStreamConfigs(
      dcaProductionStreamConfigs.data.productionStreamConfigs,
      currentProductionVolume
    );

    // This render's settings can be the last scenario's, when the new ones were
    // asked for earlier in this update. Only the latest settings count.
    if (
      store.get(dcaProductionStreamConfigsAtom) === dcaProductionStreamConfigs
    ) {
      formsFilledForRef.current = {
        scenarioId: selectedScenario?.id ?? null,
        streams: streamsWithSettings(streamConfigs),
      };
    }

    const firstOilEntry = currentProductionVolume.volumes.find(
      (volume) => volume.oil && volume.oil > 0
    );
    const firstGasEntry = currentProductionVolume.volumes.find(
      (volume) => volume.gas && volume.gas > 0
    );
    const firstWaterEntry = currentProductionVolume.volumes.find(
      (volume) => volume.water && volume.water > 0
    );

    if (!streamConfigs) {
      return;
    }

    const forecastSettings = dcaProductionStreamConfigs.data.forecastSettings;

    // Batch all form updates
    batchedUpdates(() => {
      // The forms are about to hold the scenario's parameters, not a fit's, so
      // Save uses the production selected now. This runs on a scenario change
      // and on a source change, which rereads the production.
      setFittedProduction(null);
      setForecastDuration(forecastSettings.durationPeriod);
      setForecastDurationOption(forecastSettings.durationOption);
      setQiFormat(forecastSettings.qiFormat === 'monthly' ? 'months' : 'days');
      setDiFormat(forecastSettings.diFormat);

      const initialLockedParams = getInitialLockedParameters(streamConfigs);

      const updatedLockedParams = {
        oil: {
          dca: {
            ...initialLockedParams.oil.dca,
            ...lockedParams.oil.dca,
          },
          rampUp: {
            ...initialLockedParams.oil.rampUp,
            ...lockedParams.oil.rampUp,
          },
        },
        gas: {
          dca: {
            ...initialLockedParams.gas.dca,
            ...lockedParams.gas.dca,
          },
          rampUp: {
            ...initialLockedParams.gas.rampUp,
            ...lockedParams.gas.rampUp,
          },
        },
        water: {
          dca: {
            ...initialLockedParams.water.dca,
            ...lockedParams.water.dca,
          },
          rampUp: {
            ...initialLockedParams.water.rampUp,
            ...lockedParams.water.rampUp,
          },
        },
      };

      setLockParams(updatedLockedParams);

      // Populate oil model
      if (streamConfigs.oilConfig) {
        const { dca, rampUp } = streamConfigs.oilConfig;

        if (dca) {
          const declineModel = dca.model as
            'exponential' | 'hyperbolic' | 'modified_hyperbolic' | 'ratio';

          const rampupModel = rampUp
            ? (rampUp.model as 'linear' | 'constant' | 'exponential')
            : 'none';

          const { qi, di, bFactor, dlim, m, ri, tElf } = dca.lockedParameters;
          const qiRampup = rampUp?.lockedParameters?.qi;
          const n = rampUp?.lockedParameters?.n;

          const initialOilFormValues = {
            declineModel,
            rampupModel,
            forecastStartDate: firstOilEntry
              ? parseMomentUtc(firstOilEntry.date).toDate()
              : null,
            qi: qi ?? null,
            di: di ?? null,
            bFactor: bFactor ?? null,
            dlim: dlim ?? null,
            m: m ?? null,
            ri: ri ?? null,
            tElf: tElf ?? null,
            qiRampup: qiRampup ?? null,
            rampupDuration: null,
            n: n ?? null,
          };

          setOilForm(initialOilFormValues);
          setInitialOilModelValues(initialOilFormValues);
        }
      }

      // Populate gas model
      if (streamConfigs.gasConfig) {
        const { dca, rampUp } = streamConfigs.gasConfig;

        if (dca) {
          const declineModel = dca.model as
            'exponential' | 'hyperbolic' | 'modified_hyperbolic' | 'ratio';

          const rampupModel = rampUp
            ? (rampUp.model as 'linear' | 'constant' | 'exponential')
            : 'none';

          const { qi, di, bFactor, dlim, m, ri, tElf } = dca.lockedParameters;
          const qiRampup = rampUp?.lockedParameters?.qi;
          const n = rampUp?.lockedParameters?.n;

          const initialGasFormValues = {
            declineModel,
            rampupModel,
            forecastStartDate: firstGasEntry
              ? parseMomentUtc(firstGasEntry.date).toDate()
              : null,
            qi: qi ?? null,
            di: di ?? null,
            bFactor: bFactor ?? null,
            dlim: dlim ?? null,
            m: m ?? null,
            ri: ri ?? null,
            tElf: tElf ?? null,
            qiRampup: qiRampup ?? null,
            rampupDuration: null,
            n: n ?? null,
          };

          setGasForm(initialGasFormValues);
          setInitialGasModelValues(initialGasFormValues);
        }
      }

      // Populate water model
      if (streamConfigs.waterConfig) {
        const { dca, rampUp } = streamConfigs.waterConfig;

        if (dca) {
          const declineModel = dca.model as
            'exponential' | 'hyperbolic' | 'modified_hyperbolic' | 'ratio';

          const rampupModel = rampUp
            ? (rampUp.model as 'linear' | 'constant' | 'exponential')
            : 'none';

          const { qi, di, bFactor, dlim, m, ri, tElf } = dca.lockedParameters;
          const qiRampup = rampUp?.lockedParameters?.qi;
          const n = rampUp?.lockedParameters?.n;

          const initialWaterFormValues = {
            declineModel,
            rampupModel,
            forecastStartDate: firstWaterEntry
              ? parseMomentUtc(firstWaterEntry.date).toDate()
              : null,
            qi: qi ?? null,
            di: di ?? null,
            bFactor: bFactor ?? null,
            dlim: dlim ?? null,
            m: m ?? null,
            ri: ri ?? null,
            tElf: tElf ?? null,
            qiRampup: qiRampup ?? null,
            rampupDuration: null,
            n: n ?? null,
          };

          setWaterForm(initialWaterFormValues);
          setInitialWaterModelValues(initialWaterFormValues);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dcaProductionStreamConfigs, currentProductionVolume]);

  // The selected DCA scenario's forecasts for the well, newest first. The PDP
  // Forecast Versions list shows the same ones.
  const insightsScenarioId =
    !isDummyScenario && typeof selectedScenario?.id === 'number'
      ? selectedScenario.id
      : undefined;
  const { searchAtom: scenarioForecastsAtom } =
    useProductionForecastsVersionsAtom(
      insightsPdpForecastVersionsHref,
      uwi,
      false,
      dcaWorkspaceId !== undefined,
      insightsScenarioId
    );
  // Subscribing runs the search, once per scenario.
  const scenarioForecasts = useAtomValue(scenarioForecastsAtom);

  // Opens the editor on a forecast from the selected DCA scenario without the
  // user clicking "Apply", and does so again when the scenario changes: the
  // forecast the host passed when it is in the scenario, otherwise the
  // scenario's newest. It waits for the forms to hold the scenario's settings,
  // since filling them replaces the forecast's parameters. The effect that
  // fills them runs first, and this runs again whenever the settings or the
  // production it fills them from change. Each filling replaces the forecast,
  // so the forecast is applied again after each one: when the user comes back
  // to a scenario, or when the scenario's production arrives after its
  // settings and the forms are filled from it.
  const scenarioForecastAppliedForRef = useRef<FormsFill | null>(null);
  useEffect(() => {
    const formsFilledFor = formsFilledForRef.current;
    if (!uwi || insightsScenarioId === undefined) return;
    if (formsFilledFor?.scenarioId !== insightsScenarioId) return;
    if (scenarioForecastAppliedForRef.current === formsFilledFor) return;
    const { loading, data, error } = scenarioForecasts;
    if (loading || (!data && !error)) return;

    scenarioForecastAppliedForRef.current = formsFilledFor;

    // Filling the forms from the scenario's settings leaves a stream the
    // scenario has no settings for as it was, which would keep the last
    // scenario's forecast drawn, and the forecast may have no parameters for
    // it either. Such a stream is emptied.
    const setFormByStream = {
      oil: setOilForm,
      gas: setGasForm,
      water: setWaterForm,
    };
    MODEL_STREAMS.filter(
      (stream) => !formsFilledFor.streams.includes(stream)
    ).forEach((stream) => setFormByStream[stream](defaultModelFormValues));

    if (!data) {
      setDcaNotification(SCENARIO_FORECAST_NOTIFICATIONS, [
        {
          id: 'scenario-forecasts-error',
          title: 'PDP forecasts',
          message: `The ${selectedScenario?.name ?? 'selected'} DCA scenario's forecasts for this well could not be loaded, so none was applied.`,
          type: 'error',
        },
      ]);
      return;
    }

    const forecast =
      data.results.find((f) => String(f.versionId) === String(dcaForecastId)) ??
      data.results[0];

    if (!forecast) {
      setDcaNotification(SCENARIO_FORECAST_NOTIFICATIONS, [
        {
          id: 'no-scenario-forecast',
          title: 'No PDP forecast',
          message: `The ${selectedScenario?.name ?? 'selected'} DCA scenario has no forecast for this well.`,
          type: 'info',
        },
      ]);
      return;
    }

    clearDcaGroupNotifications(SCENARIO_FORECAST_NOTIFICATIONS);
    applyPdpForecast({
      uwi,
      versionId: String(forecast.versionId),
      source: 'Insights',
    });
  }, [
    uwi,
    insightsScenarioId,
    dcaProductionStreamConfigs,
    currentProductionVolume,
    scenarioForecasts,
    dcaForecastId,
    selectedScenario?.name,
    setOilForm,
    setGasForm,
    setWaterForm,
    setDcaNotification,
    clearDcaGroupNotifications,
    applyPdpForecast,
  ]);

  const handleApplyPdpForecastVersion = (
    version: ForecastVersion,
    declineType?: 'PDP' | 'TC'
  ) => {
    applyPdpForecast({
      uwi,
      versionId: version.versionId,
      source: version.source,
      declineType,
    });
  };

  const handleResetDeclineCurveAnalysis = useCallback(() => {
    batchedUpdates(() => {
      resetEurMetricResponse();
      resetDeclineCurveMetricResponse();
      resetDeclineCurveAnalysis();
      resetRegressionData();
      setResetTable(true);
    });
  }, [
    resetEurMetricResponse,
    resetDeclineCurveMetricResponse,
    resetDeclineCurveAnalysis,
    resetRegressionData,
    setResetTable,
  ]);

  const handleSelectedPdpVersionsChange = useCallback(
    (versions: ForecastVersion[], declineType?: 'PDP' | 'TC') => {
      if (versions.length === 0) {
        setPdpVersionsRequestParams(null);
        return;
      }
      setPdpVersionsRequestParams({
        uwi,
        versions,
        requestId: versions
          .map((v) => v.id)
          .sort()
          .join(','),
        declineType,
      });
    },
    [uwi, setPdpVersionsRequestParams]
  );

  const pdpVersionsRequestParams = useAtomValue(pdpVersionsRequestParamsAtom);

  useEffect(() => {
    if (
      !pdpVersionsRequestParams ||
      pdpVersionsRequestParams.versions.length === 0
    ) {
      setPdpVersionsForecastData([]);
      return;
    }
    const {
      uwi: reqUwi,
      versions,
      requestId,
      declineType,
    } = pdpVersionsRequestParams;
    pdpVolumesRequestIdRef.current = requestId;

    const insightsVersions = versions.filter((v) => v.source === 'Insights');
    const forecastIds = insightsVersions.map((v) => String(v.versionId));
    if (forecastIds.length === 0) {
      setPdpVersionsForecastData([]);
      return;
    }

    const ac = new AbortController();
    setPdpVersionsLoading(true);

    const prodVersionId = selectedRecordVersion?.versionId ?? 'current';

    const metadataPromise = request.post<
      { pdpDeclines?: Record<string, WellPDPDeclineMetricData[]> },
      unknown
    >(
      '/queries/reservoir/eval_volumes',
      {
        query: {
          ...productionSourceParam,
          declineType,
          productionVolumes: [{ uwis: [reqUwi], versionIds: [prodVersionId] }],
          pdpForecast: [{ wellDcaForecastIds: forecastIds }],
          responseConfig: {
            includeRatiosForecast: true,
            includeCumulativeForecast: true,
            includeMbtForecast: true,
            useForecastStartAsTransition: true,
          },
        },
      },
      ac
    );

    const volumesPromise = request.post<
      { pdpDeclines?: Record<string, WellPDPDeclineMetricData[]> },
      unknown
    >(
      '/queries/reservoir/eval_volumes',
      {
        query: {
          declineType,
          productionVolumes: [{ uwis: [reqUwi], versionIds: [prodVersionId] }],
          pdpForecast: [{ wellDcaForecastIds: forecastIds }],
          responseConfig: {
            includeOnlyDcaForecast: true,
          },
        },
      },
      ac
    );

    Promise.allSettled([metadataPromise, volumesPromise]).then(
      ([metaResult, volResult]) => {
        if (ac.signal.aborted) return;
        if (pdpVolumesRequestIdRef.current !== requestId) return;
        setPdpVersionsLoading(false);

        if (
          metaResult.status !== 'fulfilled' ||
          volResult.status !== 'fulfilled'
        ) {
          setPdpVersionsForecastData([]);
          return;
        }

        const metaRes = metaResult.value;
        const volRes = volResult.value;

        if (metaRes.type !== 'success' || volRes.type !== 'success') {
          setPdpVersionsForecastData([]);
          return;
        }

        const metaData = metaRes.data?.pdpDeclines?.[reqUwi];
        const volData = volRes.data?.pdpDeclines?.[reqUwi];

        if (!metaData?.length) {
          setPdpVersionsForecastData([]);
          return;
        }

        const versionById = new Map<string, ForecastVersion>();
        versions.forEach((v) => {
          if (v.versionId !== null) {
            versionById.set(String(v.versionId), v);
          }
        });

        const forecastDataList = metaData
          .map((item: WellPDPDeclineMetricData, i: number) => {
            const versionId =
              item.wellDcaForecastId ?? item.pdpForecastVersionId ?? 'unknown';
            const version = versionById.get(String(versionId));
            const volumes = volData?.[i]?.volumes ?? [];
            if (volumes.length === 0) return null;
            return {
              id: `pdp-version-${versionId}`,
              name: version?.name ?? String(versionId),
              volume: volumes,
              cumulative: item.cumulatives ?? [],
              ratio: item.ratios ?? [],
              materialBalanceTime: item.mbts ?? [],
              isDraggable: false,
            };
          })
          .filter((x: ForecastData | null) => x !== null) as ForecastData[];

        setPdpVersionsForecastData(forecastDataList);
      }
    );

    return () => ac.abort();
  }, [
    pdpVersionsRequestParams,
    setPdpVersionsForecastData,
    selectedRecordVersion?.versionId,
    productionSourceParam,
  ]);

  useEffect(() => {
    if (selectedRecordVersion?.versionId && uwi) {
      setConvertToHyperbolicError([]);
      setVolumeMetricResponse({
        url: '/queries/reservoir/eval_volumes',
        type: 'POST',
        data: {
          query: {
            ...productionSourceParam,
            productionVolumes: [
              {
                uwis: [uwi],
                versionIds: [selectedRecordVersion?.versionId],
              },
            ],
            responseConfig: {
              includeRatiosProduction: true,
              includeMbtProduction: true,
              includeCumulativeProduction: true,
            },
          },
        },
      });
    }
  }, [
    uwi,
    selectedRecordVersion?.versionId,
    productionSourceParam,
    setVolumeMetricResponse,
    setConvertToHyperbolicError,
  ]);

  useEffect(() => {
    if (selectedRecordVersion?.versionId && uwi) {
      setProductionOnlyMetricResponse({
        url: '/queries/reservoir/eval_volumes',
        type: 'POST',
        data: {
          query: {
            ...productionSourceParam,
            productionVolumes: [
              {
                uwis: [uwi],
                versionIds: [selectedRecordVersion?.versionId],
              },
            ],
            responseConfig: {
              aggregationMetricsToInclude: [
                'recovered',
                'total_gor',
                'total_watercut',
                'total_wgr',
                'total_wor',
                'total_cgr',
              ],
            },
          },
        },
      });
    }
  }, [
    uwi,
    selectedRecordVersion?.versionId,
    productionSourceParam,
    setProductionOnlyMetricResponse,
  ]);

  useEffect(() => {
    if (selectedRecordVersion?.versionId && uwi && isDcaForecastDirty) {
      setConvertToHyperbolicError([]);
      debouncedSetDeclineCurveMetricResponse({
        url: '/queries/reservoir/eval_volumes',
        type: 'POST',
        data: {
          query: {
            ...productionSourceParam,
            pdpForecast: [
              {
                dcaForecast: dcaForecast,
              },
            ],
            productionVolumes: [
              {
                uwis: [uwi],
                versionIds: [selectedRecordVersion?.versionId],
              },
            ],
            gas_transition_date:
              dcaForecast[0].dcaParams.gasDcaParams?.forecastStartDate,
            water_transition_date:
              dcaForecast[0].dcaParams.waterDcaParams?.forecastStartDate,
            oil_transition_date:
              dcaForecast[0].dcaParams.oilDcaParams?.forecastStartDate,
            responseConfig: {
              includeRatiosForecast: true,
              includeCumulativeForecast: true,
              includeMbtForecast: true,
            },
          },
        },
      });
    }
  }, [
    setConvertToHyperbolicError,
    uwi,
    selectedRecordVersion?.versionId,
    productionSourceParam,
    debouncedSetDeclineCurveMetricResponse,
    dcaForecast,
    isDcaForecastDirty,
  ]);

  useEffect(() => {
    if (selectedRecordVersion?.versionId && uwi && isValidDcaForecast) {
      setConvertToHyperbolicError([]);
      debouncedSetEurMetricResponse({
        url: '/queries/reservoir/eval_volumes',
        type: 'POST',
        data: {
          query: {
            ...productionSourceParam,
            pdpForecast: [
              {
                dcaForecast: dcaForecast,
              },
            ],
            productionVolumes: [
              {
                uwis: [uwi],
                versionIds: [selectedRecordVersion?.versionId],
              },
            ],
            responseConfig: {
              aggregationMetricsToInclude: [
                'recovered',
                'eur',
                'remaining',
                'total_gor',
                'total_watercut',
                'total_wgr',
                'total_cgr',
                'total_wor',
              ],
            },
          },
        },
      });
    }
  }, [
    setConvertToHyperbolicError,
    uwi,
    selectedRecordVersion?.versionId,
    productionSourceParam,
    debouncedSetEurMetricResponse,
    dcaForecast,
    isValidDcaForecast,
  ]);

  const productionVolumeMetricData =
    volumeMetricResponse?.pdpDeclines[uwi]?.[0];

  const handleLegendClick = (legend: MineralType) => {
    if (selectedLegend.includes(legend)) {
      setSelectedLegend(selectedLegend.filter((l) => l !== legend));
    } else {
      setSelectedLegend([...selectedLegend, legend]);
    }
  };

  const handleDragEnd = useCallback(
    (date: Date, stream: MineralType | 'gor' | 'wor' | 'wgr' | 'cgr') => {
      batchedUpdates(() => {
        const declineModelMap = {
          oilDeclineModel: formData.oilDeclineModel,
          gasDeclineModel: formData.gasDeclineModel,
          gorDeclineModel: formData.gasDeclineModel,
          worDeclineModel: formData.waterDeclineModel,
          wgrDeclineModel: formData.waterDeclineModel,
          cgrDeclineModel: formData.gasDeclineModel,
          waterDeclineModel: formData.waterDeclineModel,
        };

        if (declineModelMap[`${stream}DeclineModel`].value === 'ratio') {
          if (oilIsPrimary || gasIsPrimary) {
            if (oilIsPrimary) {
              oilSetStartDate(date);
            } else {
              gasSetStartDate(date);
            }
          }
          return;
        }

        if (stream === 'oil') {
          oilSetStartDate(date);
        } else if (stream === 'gas' || stream === 'gor') {
          gasSetStartDate(date);
        } else {
          waterSetStartDate(date);
        }

        if (
          (stream === 'oil' || stream === 'gas' || stream === 'water') &&
          isLocked(lockedParams, stream, 'dca', 'forecastStartDate')
        ) {
          lockParamsAction({
            action: 'lock',
            stream: stream,
            type: 'dca',
            parameter: 'forecastStartDate',
            value: parseMomentUtc(date).format(SERVER_DATE_FORMAT),
          });
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      oilSetStartDate,
      gasSetStartDate,
      waterSetStartDate,
      formData.gasDeclineModel.value,
      formData.oilDeclineModel.value,
      formData.waterDeclineModel.value,
      formData.isOilModelValid,
      formData.isGasModelValid,
      lockedParams,
    ]
  );

  const handleDraggableSeriesUpdate = useCallback(
    (updatedSeries: SeriesDragUpdate) => {
      batchedUpdates(() => {
        const streamMap = draggableSeriesMap[updatedSeries.stream];
        const round2 = (n: number) => Math.round(n * 100) / 100;
        if (
          'ri' in streamMap &&
          (updatedSeries.stream === 'gor' ||
            updatedSeries.stream === 'wor' ||
            updatedSeries.stream === 'wgr' ||
            updatedSeries.stream === 'cgr')
        ) {
          const isGOR = updatedSeries.stream === 'gor';
          const isCGR = updatedSeries.stream === 'cgr';
          const invertForGOR = isGOR && gasIsPrimary;
          const invertForCGR = isCGR && oilIsPrimary;

          streamMap.ri((oldRi: number | null) => {
            if (isNil(oldRi)) return oldRi;

            const shouldInvert = invertForGOR || invertForCGR;
            const num = shouldInvert ? updatedSeries.oldY : updatedSeries.newY;
            const den = shouldInvert ? updatedSeries.newY : updatedSeries.oldY;

            if (!isFinite(num) || !isFinite(den) || den === 0) return oldRi;

            const factor = num / den;
            return round2(oldRi * factor);
          });

          streamMap.tElf((oldTElf: number | null) => {
            if (!isNil(oldTElf)) {
              // NOTE: The first time we load a DCA scenario + fit streams, the computed tElf field contains a *string* (due to `formatDecimal` in atoms.ts), which can result in NaN in Math.round() below. This happens in the other fields below too!!
              const numTElf = Number(oldTElf);
              if (isNaN(numTElf)) return oldTElf;
              return Math.max(
                1,
                Math.round(numTElf + (updatedSeries.newX - updatedSeries.oldX))
              );
            }
            return oldTElf;
          });
        } else if ('qi' in streamMap) {
          streamMap.qi((oldQi: number | null) => {
            if (!isNil(oldQi)) {
              // Dragging scales qi by how far the point moved, so a point that
              // started at zero (a month with no production) would divide by
              // zero and leave qi as Infinity or NaN. Same guard the ri branch
              // above uses.
              const { newY, oldY } = updatedSeries;
              if (!isFinite(newY) || !isFinite(oldY) || oldY === 0) {
                return oldQi;
              }
              return Math.round(oldQi * (newY / oldY));
            }
            return oldQi;
          });

          if (streamMap.rampupModel.value !== 'none') {
            streamMap.rampupDuration((oldRampupDuration: number | null) => {
              if (!isNil(oldRampupDuration)) {
                return Math.round(
                  oldRampupDuration + (updatedSeries.newX - updatedSeries.oldX)
                );
              }
              return oldRampupDuration;
            });
          }
        }
      });
    },
    [draggableSeriesMap, gasIsPrimary, oilIsPrimary]
  );

  const production = useMemo(() => {
    // Daily rows bring their own running totals, ratios and material balance
    // time, so every chart and x-axis option lines up with the days.
    if (showDailyRows) {
      return {
        volume: chartProductionVolume?.volumes ?? [],
        cumulative: chartProductionVolume?.cumulatives ?? [],
        ratio: chartProductionVolume?.ratios ?? [],
        materialBalanceTime: chartProductionVolume?.mbts ?? [],
      };
    }
    return {
      volume: chartProductionVolume?.volumes ?? [],
      cumulative: productionVolumeMetricData?.cumulatives ?? [],
      ratio: productionVolumeMetricData?.ratios ?? [],
      materialBalanceTime: productionVolumeMetricData?.mbts ?? [],
    };
  }, [showDailyRows, chartProductionVolume, productionVolumeMetricData]);

  const forecasts = useMemo(() => {
    const primary = {
      id: 'primary-forecast',
      name: 'Forecast',
      volume: declineCurveAnalysisVolumes ?? [],
      cumulative: declineCurveMetricData?.cumulatives ?? [],
      ratio: declineCurveMetricData?.ratios ?? [],
      materialBalanceTime: declineCurveMetricData?.mbts ?? [],
      isDraggable: true,
    };
    return [primary, ...pdpVersionsForecastData];
  }, [
    declineCurveAnalysisVolumes,
    declineCurveMetricData?.cumulatives,
    declineCurveMetricData?.ratios,
    declineCurveMetricData?.mbts,
    pdpVersionsForecastData,
  ]);

  const isGraphLoading =
    isProductionLoading ||
    isVolumeMetricLoading ||
    isDeclineCurveAnalysisLoading ||
    declineCurveMetricLoading ||
    regressionData.loading ||
    typeCurveVolumeMetricResponse.loading ||
    pdpVersionsLoading;

  // A parameter edit fires several requests that finish at different times, so
  // `isGraphLoading` goes on and off repeatedly while one edit settles. The
  // charts only cover themselves when there is nothing drawn yet; after that
  // they keep the last curve up and swap it when the new one arrives.
  const hasChartData =
    production.volume.length > 0 ||
    forecasts.some((forecast) => forecast.volume.length > 0);
  const isGraphFirstLoad = isGraphLoading && !hasChartData;

  const isError = !!(
    (!isGraphLoading &&
      (declineCurveAnalysisError?.type ||
        declineCurveMetricError?.type ||
        volumeMetricError?.type ||
        declineCurveAnalysisPartialErrors?.length > 0 ||
        declineCurveMetricPartialError?.length > 0 ||
        eurMetricResponsePartialError?.length > 0 ||
        convertToHyperbolicError?.length > 0)) ||
    regressionData.error
  );

  const draggableSeriesConfig = useMemo(() => {
    return {
      oil: {
        isXDraggable:
          formData.oilRampupModel.value !== 'none' &&
          formData.oilDeclineModel.value !== 'ratio',
        isYDraggable: formData.oilDeclineModel.value !== 'ratio',
        offset: formData.oilRampupDuration.value ?? 0,
      },
      gas: {
        isXDraggable:
          formData.gasRampupModel.value !== 'none' &&
          formData.gasDeclineModel.value !== 'ratio',
        isYDraggable: formData.gasDeclineModel.value !== 'ratio',
        offset: formData.gasRampupDuration.value ?? 0,
      },
      water: {
        isXDraggable:
          formData.waterRampupModel.value !== 'none' &&
          formData.waterDeclineModel.value !== 'ratio',
        isYDraggable: formData.waterDeclineModel.value !== 'ratio',
        offset: formData.waterRampupDuration.value ?? 0,
      },
      gor: {
        isXDraggable:
          formData.oilDeclineModel.value === 'ratio' ||
          formData.gasDeclineModel.value === 'ratio',
        isYDraggable:
          formData.oilDeclineModel.value === 'ratio' ||
          formData.gasDeclineModel.value === 'ratio',
        offset: 0,
      },
      wor: {
        isXDraggable: formData.waterDeclineModel.value === 'ratio',
        isYDraggable: formData.waterDeclineModel.value === 'ratio',
        offset: 0,
      },
      wgr: {
        isXDraggable:
          primaryStream === 'gas' &&
          formData.waterDeclineModel.value === 'ratio',
        isYDraggable:
          primaryStream === 'gas' &&
          formData.waterDeclineModel.value === 'ratio',
        offset: 0,
      },
      cgr: {
        isXDraggable:
          primaryStream === 'gas' &&
          (formData.oilDeclineModel.value === 'ratio' ||
            formData.gasDeclineModel.value === 'ratio'),
        isYDraggable:
          primaryStream === 'gas' &&
          (formData.oilDeclineModel.value === 'ratio' ||
            formData.gasDeclineModel.value === 'ratio'),
        offset: 0,
      },
    };
  }, [
    formData.oilRampupModel.value,
    formData.oilRampupDuration.value,
    formData.oilDeclineModel.value,
    formData.gasRampupModel.value,
    formData.gasRampupDuration.value,
    formData.gasDeclineModel.value,
    formData.waterRampupModel.value,
    formData.waterDeclineModel.value,
    formData.waterRampupDuration.value,
    primaryStream,
  ]);

  const producingWellBore = useMemo(() => {
    if (
      !wellboresData.loading &&
      wellboresData.result &&
      wellboresData.result?.type === 'success'
    ) {
      const producingWellBore = wellboresData.result.data.results.find(
        (wellbore) =>
          wellbore.uwi === uwi && wellbore.completionType === 'Producing'
      );

      if (producingWellBore) {
        return producingWellBore;
      }
    }

    return null;
  }, [wellboresData, uwi]);

  useEffect(() => {
    if (
      selectedTypeCurve?.uniqueTcId &&
      selectedScenario?.id &&
      uwi &&
      !isDummyScenario
    ) {
      clearDcaGroupNotifications('type-curves');
      if (!producingWellBore?.perforatedLateralLengthFt) {
        setDcaNotifications('type-curves', [
          {
            id: 'type-curve-error' + Date.now(),
            title: 'Type Curve Error',
            message: 'Perforated lateral length is required',
            type: 'error' as const,
          },
        ]);
        return;
      }

      const dcaScenarioId = selectedScenario.id;
      fetchScaledTypeCurveVolumes({
        url: '/queries/reservoir/bulk_type_curves',
        type: 'POST',
        data: {
          bulkTypeCurves: [
            {
              uwi: uwi,
              typeCurveHeaderDetailsId:
                selectedTypeCurve.typeCurveHeaderDetailsId,
              dcaScenarioId,
              perforatedLateralLengthFt:
                producingWellBore.perforatedLateralLengthFt,
            },
          ],
          responseConfig: {
            scaling: true,
            include_volumes: true,
            include_cumulatives: true,
            include_ratios: true,
            include_mbts: true,
            aggregation_metrics_to_include: [
              'eur',
              'total_gor',
              'total_watercut',
              'total_wgr',
              'total_cgr',
              'total_wor',
            ],
          },
        },
        onError: (error) => {
          setDcaNotifications('type-curves', [
            {
              id: 'type-curve-error' + Date.now(),
              title: 'Type Curve Error',
              message: error?.error ?? 'Error fetching type curve data',
              type: 'error' as const,
            },
          ]);
        },
      });
    }
  }, [
    uwi,
    fetchScaledTypeCurveVolumes,
    selectedTypeCurve?.uniqueTcId,
    selectedTypeCurve?.typeCurveHeaderDetailsId,
    selectedScenario?.id,
    setDcaNotifications,
    clearDcaGroupNotifications,
    producingWellBore,
    isDummyScenario,
  ]);

  const { typeCurveVolumes, typeCurveMetricData } = useMemo(() => {
    if (
      typeCurveVolumeMetricResponse.result?.type === 'success' &&
      selectedTypeCurve?.uniqueTcId
    ) {
      const typeCurveData =
        typeCurveVolumeMetricResponse.result.data.results.find(
          (result) => result?.uniqueTcId === selectedTypeCurve.uniqueTcId
        );

      if (typeCurveData) {
        const errors = typeCurveData?.errors ?? [];

        if (errors.length > 0) {
          const messages = errors.map((error, index) => ({
            id: 'type-curve-error-' + index,
            title: 'Type Curve Error',
            message: error.message,
            type: 'error' as const,
          }));

          setDcaNotifications('type-curves', messages);
        } else {
          clearDcaGroupNotifications('type-curves');
        }

        return {
          typeCurveVolumes: {
            volume: (typeCurveData?.volumes ?? []) as TypeCurveVolumeRecord[],
            cumulative: (typeCurveData?.cumulatives ??
              []) as TypeCurveVolumeRecord[],
            ratio: (typeCurveData?.ratios ?? []) as TypeCurveRatioRecord[],
            materialBalanceTime: (typeCurveData?.mbts ??
              []) as TypeCurveVolumeRecord[],
          },
          typeCurveMetricData: typeCurveData?.aggregationMetrics,
        };
      }
    }

    return {
      typeCurveVolumes: null,
      typeCurveMetricData: null,
    };
  }, [
    typeCurveVolumeMetricResponse,
    selectedTypeCurve?.uniqueTcId,
    setDcaNotifications,
    clearDcaGroupNotifications,
  ]);

  const normalizedSelectedLegend = useMemo(() => {
    return selectedLegend
      .filter((item) => item === 'oil' || item === 'water' || item === 'gas')
      .map((legend) => legend.toLowerCase() as MineralType);
  }, [selectedLegend]);

  const ratioLegend = useMemo(() => {
    return selectedLegend
      .filter(
        (item) =>
          item === 'GOR' || item === 'WOR' || item === 'WGR' || item === 'CGR'
      )
      .map((item) => item.toLowerCase() as 'gor' | 'wor' | 'wgr' | 'cgr');
  }, [selectedLegend]);

  useEffect(() => {
    if (!selectedScenario?.id || !uwi || !selectedRecordVersion?.versionId)
      return;

    if (!isDummyScenario) {
      setEarlyTimeStreams([]);
      clearDcaGroupNotifications('early-time-streams');

      fetchEarlyTimeStreams({
        type: 'POST',
        url: '/reservoir/dca_regressions',
        data: {
          dcaRegression: {
            // Checked against the production the editor shows.
            productionSource,
            productionVolumes: [
              {
                uwis: [uwi],
                versionIds: [selectedRecordVersion?.versionId],
              },
            ],
            bulkDcaRegression: [
              {
                uwi,
                dcaScenarioId: selectedScenario.id,
                checkEarlyTimeCutOff: true,
              },
            ],
          },
        },
        onSuccess: (data) => {
          const result = data.bulkDcaRegressionResults.find(
            (result) => result.uwi === uwi
          );

          setEarlyTimeStreams(result?.earlyTimeStreams ?? []);

          if (result?.errors && result?.errors.length > 0) {
            const messages = result.errors.map((error: any, index: number) => ({
              id: `early-time-streams-error-${index}`,
              title: 'Early Time DCA',
              message: error.message,
              type: (error.code === 'INFORMATION_MESSAGE'
                ? 'info'
                : 'error') as NotificationKind,
            }));

            if (messages && messages.length > 0) {
              setDcaNotification('early-time-streams', messages);
            }
          }
        },
        onError: (error: any) => {
          if (error.errors && error.errors.length > 0) {
            setDcaNotification(
              'early-time-streams',
              error.errors.map((error: any, index: number) => ({
                id: `early-time-streams-error-${index}`,
                title: 'Early Time DCA',
                message: error.message,
                type: 'error',
              }))
            );
          } else {
            setDcaNotification('early-time-streams', [
              {
                id: 'early-time-streams-error',
                title: 'Early Time DCA',
                message: error?.error ?? 'Error fetching early time streams',
                type: 'error',
              },
            ]);
          }
        },
      });
    }
  }, [
    selectedScenario?.id,
    selectedRecordVersion?.versionId,
    productionSource,
    uwi,
    clearDcaGroupNotifications,
    setDcaNotification,
    fetchEarlyTimeStreams,
    isDummyScenario,
  ]);

  // Update notifications whenever errors or warnings change
  useEffect(() => {
    const notifications: Array<{
      id: string;
      title: string;
      message: string;
      type: 'info' | 'error' | 'warning' | 'success';
    }> = [];

    // Handle warnings
    if (regressionData.warning) {
      const warningMessages = regressionData.warning
        .split('\n')
        .filter((warning) => warning.trim() !== '');

      warningMessages.forEach((warning, index) => {
        notifications.push({
          id: `warning-${index}-${Date.now()}`,
          title: 'Information',
          message: warning,
          type: 'info' as const,
        });
      });
    }

    // Handle errors
    if (isError) {
      const allErrors = [
        declineCurveAnalysisError,
        declineCurveMetricError,
        volumeMetricError,
        ...declineCurveAnalysisPartialErrors.map((error) => {
          return {
            message: {
              error: `Stream: ${error.details.streamType}, Field : ${error.details.field}, Message: ${error.message}`,
            },
          };
        }),
        ...declineCurveMetricPartialError.map((error) => {
          return {
            message: {
              error: `Stream: ${error.details.streamType}, Field : ${error.details.field}, Message: ${error.message}`,
            },
          };
        }),
        ...eurMetricResponsePartialError.map((error) => {
          return {
            message: {
              error: `Stream: ${error.details.streamType}, Field : ${error.details.field}, Message: ${error.message}`,
            },
          };
        }),
        ...convertToHyperbolicError.map((error) => {
          return {
            message: {
              error: `Stream: ${error.details.streamType}, Field : ${error.details.field}, Message: ${error.message}`,
            },
          };
        }),
        ...(regressionData.error
          ? regressionData.error
              .split('\n')
              .filter((error) => error.trim() !== '')
              .map((error) => ({ message: { error } }))
          : []),
      ].filter(
        (error): error is NonNullable<typeof error> => error !== undefined
      );

      const uniqueErrorMessages = Array.from(
        new Set(
          allErrors.map((error) => {
            return 'error' in error.message
              ? error.message.error
              : `Error occurred while fetching data`;
          })
        )
      );

      uniqueErrorMessages.forEach((error, index) => {
        notifications.push({
          id: `error-${index}-${Date.now()}`,
          title: 'Error',
          message: error,
          type: 'error' as const,
        });
      });
    }

    // Reset and set new notifications (replaces old ones)
    if (notifications.length > 0) {
      setDcaNotification('dca-main', notifications);
    } else {
      clearDcaGroupNotifications('dca-main');
    }
  }, [
    isError,
    regressionData.warning,
    regressionData.error,
    declineCurveAnalysisError,
    declineCurveMetricError,
    volumeMetricError,
    declineCurveAnalysisPartialErrors,
    declineCurveMetricPartialError,
    eurMetricResponsePartialError,
    convertToHyperbolicError,
    setDcaNotification,
    clearDcaGroupNotifications,
  ]);

  const enableMarkers = useMemo(() => {
    return !!markers?.segmentationDates && !!markers?.outlierDates;
  }, [markers]);

  const fitCheckedStreams = useFitCheckedStreams({ uwi, producingWellBore });

  // `useDcaHotkeysMode` reports the selected stream, but it takes the handler
  // below as an argument, so the handler reads the stream from a ref instead.
  const hotkeysStreamRef = useRef<MineralType | null>(null);

  // Whether the charts are showing only the selected stream, and the legend to
  // restore when that is switched back off.
  const [soloStream, setSoloStream] = useState(false);
  const legendBeforeSoloRef = useRef<typeof selectedLegend | null>(null);
  const selectedLegendRef = useRef(selectedLegend);
  selectedLegendRef.current = selectedLegend;

  const handleToggleStreamSolo = useCallback(
    () => setSoloStream((prev) => !prev),
    []
  );

  // Shifts the selected stream's start date by whole months. Goes through the
  // same handler a start-date drag does, so the ratio and primary-stream rules
  // still apply.
  const handleShiftStartDate = useCallback(
    (months: number) => {
      const stream = hotkeysStreamRef.current;
      if (!stream) return;
      const current = startDateByStream[stream];
      if (!current) return;
      handleDragEnd(
        parseMomentUtc(current).add(months, 'months').toDate(),
        stream
      );
    },
    [startDateByStream, handleDragEnd]
  );

  const { mode: hotkeysMode, activeStream: hotkeysActiveScope } =
    useDcaHotkeysMode({
      enabled: enableHotkeysMode,
      onPreviousWell,
      onNextWell,
      onShiftStartDate: handleShiftStartDate,
      onFitCheckedStreams: fitCheckedStreams,
      onToggleStreamSolo: handleToggleStreamSolo,
    });

  const hotkeysStream = hotkeysActiveScope
    ? HOTKEY_SCOPE_STREAMS[hotkeysActiveScope]
    : null;
  hotkeysStreamRef.current = hotkeysStream;

  // While soloed, the charts show only the stream being edited, and switching
  // streams moves the solo with it. Turning it off puts back whatever the
  // legend held beforehand rather than blanket-enabling every stream.
  useEffect(() => {
    if (soloStream) {
      if (!legendBeforeSoloRef.current) {
        legendBeforeSoloRef.current = selectedLegendRef.current;
      }
      if (!hotkeysStream) return;
      setSelectedLegend((prev) => [
        ...prev.filter((entry) => !VOLUME_STREAMS.includes(entry as never)),
        hotkeysStream,
      ]);
      return;
    }

    if (legendBeforeSoloRef.current) {
      setSelectedLegend(legendBeforeSoloRef.current);
      legendBeforeSoloRef.current = null;
    }
  }, [soloStream, hotkeysStream]);

  // Leaving hotkeys mode must not leave the charts soloed, with nothing on
  // screen to say why streams are missing.
  useEffect(() => {
    if (!hotkeysMode.active) setSoloStream(false);
  }, [hotkeysMode.active]);

  // Per-stream UR shown on every chart, from the same EUR metrics that feed
  // the EUR table below the charts.
  const headerStats = useMemo(
    () => [
      { label: 'Oil EUR', value: combinedEurMetricData?.oilEur, unit: 'bbl' },
      { label: 'Gas EUR', value: combinedEurMetricData?.gasEur, unit: 'mcf' },
      {
        label: 'Water EUR',
        value: combinedEurMetricData?.waterEur,
        unit: 'bbl',
      },
      {
        label: 'Lateral Length',
        value: producingWellBore?.perforatedLateralLengthFt,
        unit: 'ft',
      },
    ],
    [combinedEurMetricData, producingWellBore?.perforatedLateralLengthFt]
  );

  const enableTypeCurve = useMemo(() => {
    return !!(typeCurveVolumes && typeCurveVolumes.volume.length > 0);
  }, [typeCurveVolumes]);

  return (
    <>
      <DcaNotifications />
      <HotkeysHelpOverlay />

      <div className={style.layoutGrid}>
        <div className={style.sidebar}>
          <ConfigurationPanel
            uwi={uwi}
            recordVersionHref={recordVersionHref}
            dailyRecordVersionHref={dailyRecordVersionHref}
            productionDataSources={productionDataSources}
            legacyPdpForecastVersionsHref={legacyPdpForecastVersionsHref}
            insightsPdpForecastVersionsHref={insightsPdpForecastVersionsHref}
            earlyTimeStreams={earlyTimeStreams}
            disableFitStreams={
              isProductionLoading ||
              regressionData.loading ||
              isDummyScenario ||
              dcaProductionStreamConfigs.loading ||
              !selectedScenario?.id
            }
            enableHotkeysMode={enableHotkeysMode}
            dcaWorkspaceId={dcaWorkspaceId}
            onReset={handleResetDeclineCurveAnalysis}
            onApplyPdpForecastVersion={handleApplyPdpForecastVersion}
            onSelectedPdpVersionsChange={handleSelectedPdpVersionsChange}
            producingWellBore={producingWellBore}
            dcaForecast={dcaForecast[0] ?? null}
            onForecastSaved={onForecastSaved}
            saveAction={saveAction}
          />
        </div>
        <div className={style.contentArea}>
          <ChartExportSection>
            <div
              className={classNames(style.chartsContainer, {
                [style.chartsContainerExpanded]: !!expandedChart,
              })}
            >
              {CHART_PANELS.map(({ type, title }) => {
                if (expandedChart && expandedChart !== type) return null;
                const isExpanded = expandedChart === type;
                // The monthly chart is the one the daily toggle rescales, so
                // its name follows the toggle.
                const panelTitle =
                  type === 'monthly' && volumeDisplayMode === 'daily'
                    ? 'Daily Volumes'
                    : title;
                return (
                  <div key={type} className={style.chartItem}>
                    <VolumeChart2
                      title={panelTitle}
                      type={type}
                      production={production}
                      productionVolumeIsDailyRate={showDailyRows}
                      forecasts={forecasts}
                      isLoading={isGraphFirstLoad}
                      selectedSeries={
                        type === 'ratio'
                          ? ratioLegend
                          : normalizedSelectedLegend
                      }
                      onDragEnd={handleDragEnd}
                      onDraggableSeriesUpdate={handleDraggableSeriesUpdate}
                      draggableSeriesConfig={draggableSeriesConfig}
                      showMarkers={showMarkers}
                      segmentDates={markers?.segmentationDates}
                      outlierDates={markers?.outlierDates}
                      primaryStream={primaryStream}
                      typeCurveVolumes={typeCurveVolumes}
                      showTypeCurve={showTypeCurve}
                      volumeDisplayMode={volumeDisplayMode}
                      headerStats={headerStats}
                      xAxisType={xAxisTypes[type]}
                      initialAutoZoom
                      footer={
                        <div className={style.chartItemFooter}>
                          <XAxisSwitcher
                            chartType={type}
                            value={xAxisTypes[type]}
                            onChange={(value) =>
                              setXAxisTypes((prev) => ({
                                ...prev,
                                [type]: value,
                              }))
                            }
                          />
                        </div>
                      }
                      exportConfig={{
                        label: title,
                        filenamePrefix: kebabCase(title),
                      }}
                      header={
                        <div className={style.chartItemActions}>
                          <Button
                            kind="ghost"
                            size="sm"
                            hasIconOnly
                            renderIcon={isExpanded ? Minimize : Maximize}
                            iconDescription={
                              isExpanded
                                ? `Collapse ${title}`
                                : `Expand ${title}`
                            }
                            tooltipPosition="left"
                            onClick={() =>
                              setExpandedChart(isExpanded ? null : type)
                            }
                          />
                        </div>
                      }
                    />
                  </div>
                );
              })}
            </div>
          </ChartExportSection>

          <ChartActions
            selectedLegend={selectedLegend}
            handleLegendClick={handleLegendClick}
            showMarkers={showMarkers}
            setShowMarkers={setShowMarkers}
            enableMarkers={enableMarkers}
            showTypeCurve={showTypeCurve}
            setShowTypeCurve={setShowTypeCurve}
            enableTypeCurve={enableTypeCurve}
            volumeDisplayMode={volumeDisplayMode}
            onVolumeDisplayModeChange={setVolumeDisplayMode}
          />

          <EurTable
            volumeMetricData={combinedEurMetricData}
            typeCurveMetricData={typeCurveMetricData}
            isLoading={
              isEurMetricLoading ||
              isProductionOnlyMetricLoading ||
              typeCurveVolumeMetricResponse.loading
            }
          />
        </div>
      </div>

      {!hideForecastTable && (
        <DeclineCurveAnalysisTable
          dcaForecast={isDcaForecastDirty ? dcaForecast : []}
          dcaForecastData={dcaForecast}
          isValidDcaForecast={isValidDcaForecast}
          uwis={uwi}
          versionId={selectedRecordVersion?.versionId ?? ''}
          productionSource={productionSource}
          wellName={wellName}
          resetTable={resetTable}
          onResetSuccess={() => setResetTable(false)}
        />
      )}
    </>
  );
};

export const DeclineCurveAnalysis = memo(DeclineCurveAnalysisImpl);
